import React, {useCallback, useEffect, useState} from 'react';
import {Button, Col, FloatButton, Form, Input, message, Modal, Row, Select} from 'antd';
import TestCreationTabs from "../components/TestCreationTabs";
import CreationEditor from "../components/CreationEditor";
import {
    CommandDto,
    ParsingControllerApi, PlaywrightControllerApi,
    PrefabControllerApi,
    PrefabDto, SystemDto,
    TestControllerApi,
    TestDto
} from "../api";
import {useNavigate, useParams} from "react-router-dom";
import {SaveOutlined, RocketOutlined} from '@ant-design/icons';
import {PredefinedVariable} from "../components/PredefinedVariablesTab";
import {getErrorMessage, setOrderIndexOfCommandDtos} from "../utils/Utils";
import {useKeycloak} from "@react-keycloak/web";
import {useKeycloakHeader} from "../config/Config";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {useDirty} from "../utils/hook/DirtyProvider";
import {useSystemContext} from "../utils/hook/SystemProvider";
import {CustomPlaywrightApi} from "../api-custom/api";

interface CreateTestProps {
    isTest?: boolean
}

const defaultProps: CreateTestProps = {
    isTest: true
}

const layout = {
    labelCol: {span: 8},
    wrapperCol: {span: 16},
};

const enum TestTypeEnum {
    KOMPONENTE = 'KOMPONENTE',
    TEST = 'TEST'
}


const CreateTest: React.FC<CreateTestProps> = (props) => {
    const commands: CommandDto[] = [];
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

    // Angezeigte Liste der Befehle
    const [abstractCommandList, setAbstractCommandList] = useState(commands);

    // Ein neuer Command wurde über die Basic-Befehle erstellt
    const [formCommandDto, setFormCommandDto] = useState<CommandDto>();

    //Callback-Commands
    const [callBackCommands, setCallBackCommands] = useState<CommandDto[]>([]);

    // Wenn ein Test/Prefab zum Bearbeiten da ist
    const [fetchedTest, setFetchedTest] = useState<TestDto>();

    // Titel der Testerstellungsseite
    const [pageTitel, setPageTitel] = useState("Test");

    const [predefinedVariables, setPredefinedVariables] = useState<{ [key: string]: string; }>({});

    const [fetchedTestType, setFetchedTestType] = useState<TestTypeEnum | undefined>(undefined);


    const {id} = useParams();
    let idNumber;
    if (typeof id === "string") {
        idNumber = parseInt(id, 10);
    }

    const testType = [TestTypeEnum.KOMPONENTE, TestTypeEnum.TEST];

    const [importForm] = Form.useForm();
    const [saveForm] = Form.useForm();
    const {Option} = Select;
    const navigate = useNavigate();

    const testControllerApi = new TestControllerApi();
    const prefabControllerApi = new PrefabControllerApi();
    const parsingControllerApi = new ParsingControllerApi();
    const playwrightApi = new CustomPlaywrightApi();

    const {keycloak} = useKeycloak();
    const {isDirty, setIsDirty} = useDirty();
    const headers = useKeycloakHeader();
    const { systems, triggerRefresh } = useSystemContext();

    const showModal = () => {
        setIsModalOpen(true);
    };

    const showSaveModal = () => {
        setIsSaveModalOpen(true);
    };

    const handleImportCodeGen = (replace: boolean) => {
        importForm.validateFields().then((values) => {
            console.log("Values", values);
            const formatedCode = values.code.replace(/\n/g, '');
            parsingControllerApi.getParsedCommands(formatedCode, headers)
                .then((axiosResponse) => {
                    const axiosData: Array<CommandDto> = axiosResponse.data as CommandDto[];
                    if (axiosResponse.status === 200) {
                        if(replace){
                            setAbstractCommandList(axiosData);
                        } else {
                            let addedCommands = [...abstractCommandList, ...axiosData];
                            setAbstractCommandList(addedCommands)
                        }


                        setIsModalOpen(false);
                        importForm.resetFields();
                    }
                })
                .catch((error) => {
                    let msg = " Quellcode konnte nicht übersetzt werden! ";
                    let errorMessage = error.response.data;
                    message.error(msg + errorMessage);
                });
        });
    };

    const handleSaveModal = () => {
        let commandDtosWithOrderIndex: CommandDto[] = setOrderIndexOfCommandDtos(callBackCommands);
        saveForm.validateFields().then((values) => {
            if (values.saveOption === "KOMPONENTE") {
                let prefabDto = {
                    name: values.creationName,
                    description: values.descriptionName,
                    system: {id: values.systemOption} as SystemDto,
                    commandDtos: commandDtosWithOrderIndex,
                    predefinedVariables: predefinedVariables
                } as PrefabDto;
                prefabControllerApi.createNewPrefab(prefabDto, headers)
                    .then((response) => {
                        let prefabDto = response.data as PrefabDto;
                        setIsSaveModalOpen(false);
                        message.success("Komponente wurde erfolgreich erstellt");
                        setIsDirty(false);
                        navigate(process.env.PUBLIC_URL + '/edit/prefab/' + prefabDto.id);
                    })
                    .catch((error) => {
                        let msg = "Komponente konnte nicht erstellt werden: ";
                        let errorMsg = msg + getErrorMessage(error);
                        if (error.response && error.response.data) {
                            errorMsg += " " + error.response.data;
                        }
                        message.error({content: errorMsg, duration: 20});
                    });
            } else {
                let testDto = {
                    name: values.creationName,
                    description: values.descriptionName,
                    system: {id: values.systemOption} as SystemDto,
                    commandDtos: commandDtosWithOrderIndex,
                    predefinedVariables: predefinedVariables
                } as TestDto;
                testControllerApi.createNewTest(testDto, headers)
                    .then(r => {
                        let testDto = r.data as TestDto;
                        setIsSaveModalOpen(false);
                        message.success("Test wurde erfolgreich erstellt");
                        setIsDirty(false);
                        navigate(process.env.PUBLIC_URL + '/edit/test/' + testDto.id);
                    })
                    .catch((error) => {
                        let msg = "Test konnte nicht erstellt werden: ";
                        let errorMsg = msg + getErrorMessage(error);
                        if (error.response && error.response.data) {
                            errorMsg += " " + error.response.data;
                        }
                        message.error({content: errorMsg, duration: 20});
                    });
            }
        });
    }

    const handleUpdateButton = () => {
        saveForm.validateFields().then((values) => {
            if (fetchedTest === undefined || fetchedTest.id === undefined) {
                message.error("Fehler: Test/Komponente konnte nicht gefunden werden!");
                return;
            }
            let commandDtosWithOrderIndex: CommandDto[] = setOrderIndexOfCommandDtos(callBackCommands);
            if (values.saveOption === "KOMPONENTE") {
                if (fetchedTestType != TestTypeEnum.KOMPONENTE) {
                    message.error("Fehler: Ein Test kann nicht in eine Komponente umgewandelt werden!");
                    return;
                }
                let prefabDto = {
                    name: values.creationName,
                    description: values.descriptionName,
                    system: {id: values.systemOption} as SystemDto,
                    id: fetchedTest.id,
                    commandDtos: commandDtosWithOrderIndex,
                    predefinedVariables: predefinedVariables
                } as PrefabDto
                prefabControllerApi.updatePrefab(prefabDto, headers)
                    .then(r => {
                        setIsSaveModalOpen(false);
                        setIsDirty(false);
                        message.success("Komponente wurde erfolgreich aktualisiert");
                    }).catch((error) => {
                    let msg = "Komponente konnte nicht aktualisiert werden: ";
                    let errorMsg = msg + getErrorMessage(error);
                    if (error.response && error.response.data) {
                        errorMsg += " " + error.response.data;
                    }
                    message.error({content: errorMsg, duration: 20});
                });
            } else {
                if (fetchedTestType != TestTypeEnum.TEST) {
                    message.error("Fehler: Eine Komponente kann nicht in ein Test umgewandelt werden!")
                    return;
                }
                let testDto = {
                    name: values.creationName,
                    description: values.descriptionName,
                    system: {id: values.systemOption} as SystemDto,
                    id: fetchedTest.id,
                    commandDtos: commandDtosWithOrderIndex,
                    predefinedVariables: predefinedVariables
                } as TestDto
                testControllerApi.updateTest(testDto, headers)
                    .then(r => {
                        setIsSaveModalOpen(false);
                        setIsDirty(false);
                        message.success("Test wurde erfolgreich aktualisiert");
                    }).catch((error) => {
                    let msg = "Test konnte nicht aktualisiert werden: ";
                    let errorMsg = msg + getErrorMessage(error);
                    if (error.response && error.response.data && error.response.status != 500) {
                        errorMsg += " " + error.response.data;
                    }
                    message.error({content: errorMsg, duration: 20});
                });
            }
        });
    }

    const fetchExistingData = () => {
        if (props.isTest) {
            testControllerApi.getTestById(idNumber, headers)
                .then((axiosResponse) => {
                    const axiosData: TestDto = axiosResponse.data;
                    setFetchedTest(axiosData);
                    setFetchedTestType(TestTypeEnum.TEST);
                    setPageTitel("Test: " + axiosData.name + " (" + axiosData.system.name + ")");
                    fillModalWithExistingTestData(axiosData);

                    if (axiosData.predefinedVariables) {
                        setPredefinedVariables(axiosData.predefinedVariables);
                    }

                    if (axiosData.commandDtos != undefined) {
                        setAbstractCommandList(axiosData.commandDtos);
                    }
                })
                .catch((error) => {
                    let msg = "Test konnte nicht geladen werden: ";
                    message.error(msg + getErrorMessage(error));
                });
        } else {
            prefabControllerApi.getPrefabById(idNumber, headers)
                .then((axiosResponse) => {
                    const axiosData: PrefabDto = axiosResponse.data;
                    setFetchedTest(axiosData);
                    setFetchedTestType(TestTypeEnum.KOMPONENTE);
                    setPageTitel("Komponente: " + axiosData.name + " (" + axiosData.system.name + ")");
                    fillModalWithExistingPrefabData(axiosData);

                    if (axiosData.predefinedVariables) {
                        setPredefinedVariables(axiosData.predefinedVariables);
                    }

                    if (axiosData.commandDtos != undefined) {
                        setAbstractCommandList(axiosData.commandDtos);
                    }


                })
                .catch((error) => {
                    let msg = "Komponente konnte nicht geladen werden: ";
                    message.error(msg + getErrorMessage(error));
                });
        }
    }

    const fillModalWithExistingTestData = (testDto: TestDto) => {
        saveForm.setFieldsValue({
            saveOption: "TEST",
            systemOption: testDto.system.id,
            creationName: testDto.name,
            descriptionName: testDto.description
        });
    }
    const fillModalWithExistingPrefabData = (prefabDto: PrefabDto) => {
        saveForm.setFieldsValue({
            saveOption: "KOMPONENTE",
            systemOption: prefabDto.system.id,
            creationName: prefabDto.name,
            descriptionName: prefabDto.description
        });
    }


    const handleCancel = () => {
        setIsModalOpen(false);
    };


    const handleSaveCancel = () => {
        setIsSaveModalOpen(false);
    };

    const handleCommandCard = (data: CommandDto) => {
        setFormCommandDto(data);
    }

    const handleCommandData = useCallback((data: CommandDto[]) => {
        setCallBackCommands(data);
        if (data.length > 0 && JSON.stringify(data) !== JSON.stringify(abstractCommandList)) {
            setIsDirty(true);
        }
    }, [setCallBackCommands, abstractCommandList]);

    useEffect(() => {
        if (id !== undefined) {
            fetchExistingData();
        }
        triggerRefresh();
    }, [id, props.isTest]);

    const addNewPredefinedVariable = (variable: PredefinedVariable) => {
        setPredefinedVariables({
            ...predefinedVariables,
            [variable.key]: variable.value,
        });
        setIsDirty(true);
    }

    const removeVariable = (key: string) => {
        // Erstelle eine Kopie des aktuellen Zustands
        const updatedVariables = {...predefinedVariables};

        // Überprüfe, ob der Key im State existiert
        if (updatedVariables.hasOwnProperty(key)) {
            // Key aus der Kopie entfernen
            delete updatedVariables[key];

            // Den State mit der aktualisierten Kopie aktualisieren
            setPredefinedVariables(updatedVariables);
        }
        setIsDirty(true);
    };

    const startCodegen = () => {
        playwrightApi.openCodegen(headers).then((response) => {
            message.info("Playwright-Codegen wird gestartet")
        }).catch((error) => {
            message.error({content: 'Bitte starte deine lokale GLOW-Anwendung, damit die Codegenerierung ausgeführt werden kann!', duration: 15});
        })
    }

    const handleReset = () => {
        setAbstractCommandList([]);
    }

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }


    return (
        <>
            <Row gutter={32}>
                <Col span={6}>
                    <TestCreationTabs predefinedVariables={predefinedVariables} onPredefinedVariables={addNewPredefinedVariable}
                                      onCommandCardData={handleCommandCard} onRemoveVariable={removeVariable}
                                      abstractCommands={abstractCommandList}/>
                </Col>
                <Col span={18}>
                    <Row justify="space-between" align="middle">
                        <Col span={10}>
                            <h1 style={{textAlign: 'left', fontWeight: 'normal'}}>{pageTitel}</h1>
                        </Col>
                        <Col style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                            {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER, ROLES.GLOW_TEST_DESIGNER], keycloak) &&
                                <>
                                  <Button onClick={showModal} style={{marginLeft: '8px'}}>Import</Button>
                                  <Button onClick={showSaveModal} type="primary" style={{marginLeft: '8px'}}>Speichern</Button>
                                </>
                            }
                            <Button onClick={handleReset} danger style={{marginLeft: '8px'}}>Zurücksetzen</Button>
                            <Button onClick={startCodegen} style={{marginLeft: '8px'}} icon={<RocketOutlined/>}>
                                Testgenerierung
                            </Button>
                        </Col>
                    </Row>
                    <CreationEditor abstractCommands={abstractCommandList} formCommandDto={formCommandDto}
                                    onUpdateCommandsData={handleCommandData}/>
                </Col>
            </Row>
            <Modal
                width={1000}
                title="Import CodeGen"
                open={isModalOpen}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        Abbrechen
                    </Button>,
                    <Button key="replace" type="default" onClick={() => {handleImportCodeGen(true)}}>
                        Ersetzen
                    </Button>,
                    <Button key="add" type="primary" onClick={() => {handleImportCodeGen(false)}}>
                        Hinzufügen
                    </Button>
                ]}
            >
                <Form form={importForm} name="importForm">
                    {/* Label als eigenes Element über dem Input */}
                    <span style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                    Code eingeben
                </span>
                    <Form.Item
                        name="code"
                        rules={[
                        {
                            required: true,
                            message: 'Bitte geben Sie den Code ein!',
                        },
                    ]}
                    >
                        <Input.TextArea
                            rows={28} // Mehr Platz für den Code
                            placeholder="Fügen Sie hier den generierten Java-Code ein..."
                            style={{
                                fontFamily: 'monospace', // Sieht aus wie in einem Code-Editor
                                whiteSpace: 'pre', // Erhält die Zeilenumbrüche
                                overflowX: 'auto', // Horizontales Scrollen bei langen Zeilen
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Speichere deine Kreation"
                onOk={handleSaveModal}
                onCancel={handleSaveCancel}
                open={isSaveModalOpen}
                footer={[
                    <Button
                        key="updateButton"
                        disabled={fetchedTest === undefined}
                        onClick={handleUpdateButton}
                        type={"primary"}
                    >
                        Aktualisieren
                    </Button>,
                    <Button key="cancel" onClick={handleSaveCancel}>
                        Abbrechen
                    </Button>,
                    <Button key="create" type="primary" onClick={handleSaveModal}>
                        Erstellen
                    </Button>
                ]}
            >
                <Form {...layout} form={saveForm} name="saveForm">
                    <Form.Item name="saveOption" label="Speichern" rules={[{required: true}]}>
                        <Select
                            placeholder="Wähle eine Speicherart"
                            allowClear
                        >
                            {testType.map(option => (
                                <Option key={option} value={option}>
                                    {option}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="systemOption" label="System" rules={[{required: true}]}>
                        <Select
                            placeholder="Wähle ein System"
                            allowClear
                        >
                            {systems.map(option => (
                                <Option key={option.id} value={option.id}>
                                    {option.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="creationName" label="Name" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="descriptionName" label="Beschreibung" rules={[{
                        max: 4096,
                        message: 'Die Beschreibung darf nicht länger als 4096 Zeichen sein!'
                    }]}>
                        <Input/>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );


}

export default CreateTest;